/**
 * MentionsPopover
 * ----------------------------------------------------------------------------
 * Popover de seleção de participantes para @menções no input do chat de grupos.
 *
 * - É controlado pelo pai (open + searchQuery + onSelect).
 * - Lista os participantes do grupo (com nome do contato CRM se disponível).
 * - Filtra por nome/telefone conforme o searchQuery (texto digitado após o "@").
 * - Reusa SmartAvatar para iniciais/cores consistentes.
 *
 * Espelha o comportamento do WhatsApp Web: ao digitar "@", aparece a lista
 * dos participantes; selecionar substitui o token "@<query>" por "@<Nome>".
 */

import { useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';

export interface MentionParticipant {
  /** Telefone normalizado (apenas dígitos) */
  phone: string;
  /** Nome para exibição (contato CRM, pushName ou número formatado) */
  name: string;
  /** Avatar do contato CRM (opcional) */
  avatar_url?: string | null;
}

interface MentionsPopoverProps {
  open: boolean;
  /** Texto digitado após o "@" (filtro) */
  searchQuery: string;
  /** Lista completa dos participantes do grupo */
  participants: MentionParticipant[];
  /** Callback ao selecionar um participante */
  onSelect: (participant: MentionParticipant) => void;
  /** Conteúdo filho (textarea/input) usado como anchor */
  children: React.ReactNode;
}

export function MentionsPopover({
  open,
  searchQuery,
  participants,
  onSelect,
  children,
}: MentionsPopoverProps) {
  // Filtra participantes por nome/telefone (case-insensitive)
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return participants.slice(0, 50);
    return participants
      .filter((p) => {
        const name = (p.name || '').toLowerCase();
        const phone = (p.phone || '').toLowerCase();
        return name.includes(q) || phone.includes(q);
      })
      .slice(0, 50);
  }, [participants, searchQuery]);

  return (
    <Popover open={open && filtered.length > 0}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-72 p-1"
        // Não rouba o foco do textarea ao abrir
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <ScrollArea className="max-h-64">
          <div className="flex flex-col">
            {filtered.map((p) => (
              <button
                key={p.phone}
                type="button"
                onMouseDown={(e) => {
                  // mousedown evita perda de foco do textarea antes do click
                  e.preventDefault();
                  onSelect(p);
                }}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded-md text-left',
                  'hover:bg-accent transition-colors'
                )}
              >
                <SmartAvatar
                  name={p.name}
                  src={p.avatar_url || undefined}
                  className="h-7 w-7 flex-shrink-0"
                />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-medium truncate">{p.name}</span>
                  {p.phone && (
                    <span className="text-[11px] text-muted-foreground truncate">
                      +{p.phone}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
